import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Drawer } from '@mui/material';
import useResponsive from '../../../hooks/useResponsive';
import PlatformAvatar from '../../../components/platform/PlatformAvatar';
import PlatformBrandMark from '../../../components/platform/PlatformBrandMark';
import PlatformIcon from '../../../components/platform/PlatformIcon';
import navConfig, { getActiveNavId } from './config';
import { logout } from '../../../redux/actions/auth.action';
import '../../../styles/platform-dashboard.css';

const NAV_WIDTH = 248;

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

function SidebarContent({ onNavigate }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth || {});
  const activeId = getActiveNavId(pathname);

  const displayName = user?.name || user?.displayName || user?.firstName || 'Admin';
  const displayRole = user?.companyName || user?.email || 'Organization admin';

  const handleNavigate = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const handleLogout = () => {
    dispatch(logout(navigate));
    onNavigate?.();
  };

  return (
    <aside className="dashboard-sidebar">
      <div className="dashboard-sidebar-brand">
        <PlatformBrandMark size={38} />
        <div>
          <div className="dashboard-sidebar-brand-name">Nurturer</div>
          <div className="dashboard-sidebar-brand-eyebrow">Company admin</div>
        </div>
      </div>

      <div className="dashboard-sidebar-label">Workspace</div>
      <nav className="dashboard-sidebar-nav">
        {navConfig.map((item) => {
          const isActive = item.id === activeId;

          return (
            <button
              key={item.id}
              type="button"
              className={`dashboard-sidebar-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => handleNavigate(item.path)}
            >
              <PlatformIcon name={item.icon} size={19} stroke={isActive ? 2.2 : 2} />
              <span>{item.title}</span>
            </button>
          );
        })}
      </nav>

      <div className="dashboard-sidebar-foot">
        <div className="dashboard-sidebar-user">
          <PlatformAvatar name={displayName} size={34} photoUrl={user?.photoUrl} />
          <div className="dashboard-sidebar-user-meta">
            <div className="dashboard-sidebar-user-name">{displayName}</div>
            <div className="dashboard-sidebar-user-role">{displayRole}</div>
          </div>
          <button
            type="button"
            className="dashboard-sidebar-logout"
            onClick={handleLogout}
            aria-label="Log out"
          >
            <PlatformIcon name="logout" size={17} />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const drawerPaperSx = {
    width: NAV_WIDTH,
    backgroundColor: '#23C2C4',
    borderRight: 'none',
    overflow: 'hidden',
  };

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{ sx: drawerPaperSx }}
        >
          <SidebarContent />
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: drawerPaperSx }}
        >
          <SidebarContent onNavigate={onCloseNav} />
        </Drawer>
      )}
    </Box>
  );
}
